import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  List,
  Modal,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChartLine, IconInfoCircle, IconSparkles } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import TextInput from 'src/components/design-system/input/TextInput';
import { Button as DesignSystemButton } from 'src/design-system/Button';
import { ApiError } from 'src/error/ApiError';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { showError } from 'src/toasts/toasts';
import PageLoader from 'src/UI/loader/PageLoader';
import { formatSignedMoney } from 'src/common/currency';
import { horizonAge } from './kpis';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useCashflowReadiness } from './hooks/useCashflowReadiness';
import { ModelDataCallout } from './ModelDataCallout';
import { TabShowsItsOwnEmptyState } from 'src/common/components/data-states/TabShowsItsOwnEmptyState';
import { useCreateScenario } from './hooks/useScenarioMutations';
import { useMembersOwnScenarios, useScenarios } from './hooks/useScenarios';
import { ModelViews } from './list/ModelViews';
import { atAgeStartingSentence, NEVER_RUNS_OUT_TEXT } from './moneyLasts';
import { modelDetailPath, ScenarioScope, useScenarioScope } from './scope';
import { CashflowReadiness, ScenarioSummary } from './types';

type SelectableMember = { clientId: string; name: string };

type Props = {
  // Defaults to the surrounding client scope; the family page passes its own.
  scope?: ScenarioScope;
  // Family scope only: the family's members, whose own models are listed beside the
  // household's, and who the selection modal offers when the family has more than two
  // adults (a household model covers at most two).
  members?: SelectableMember[];
};

// A 422 from the create endpoint means seeding was blocked by missing profile
// data; the response detail carries the questions to answer before retrying.
function blockedSeedQuestions(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) {
    return null;
  }
  const questions = error.detail?.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  const usable = questions.filter((question): question is string => typeof question === 'string');
  return usable.length > 0 ? usable : null;
}

export function CashflowModelsSection({ scope: scopeProp, members }: Props) {
  const contextScope = useScenarioScope();
  const scope = scopeProp ?? contextScope;
  const { flagEnabledFamilyCashflowModelling, flagEnabledFamilyCashflowsRevamp } = useFlags();
  const [showArchived, setShowArchived] = useState(false);
  // With the revamp, archived models have a tab of their own, so they are always read.
  const includeArchived = showArchived || flagEnabledFamilyCashflowsRevamp;
  const { scenarios, isLoading, isError } = useScenarios(includeArchived, scope ?? undefined);
  const memberModels = useMembersOwnScenarios(
    scope?.kind === 'family' && flagEnabledFamilyCashflowsRevamp
      ? (members ?? []).map(member => member.clientId)
      : [],
    includeArchived,
  );
  const { createScenario, isCreating } = useCreateScenario(scope ?? undefined);
  const navigate = useNavigate();
  const [createModalOpen, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false);
  const [missingDataQuestions, setMissingDataQuestions] = useState<string[] | null>(null);
  // Hooks stay above the early returns; the query itself waits for a scope.
  const { readiness } = useCashflowReadiness(scope);

  if (isLoading || memberModels.isLoading) {
    return <PageLoader absolute={false} />;
  }
  if (isError || !scope) {
    return (
      <Text c="dimmed" size="sm" px="md">
        Cashflow models could not be loaded. Please try again shortly.
      </Text>
    );
  }

  const visible = [...(scenarios ?? []), ...memberModels.scenarios].filter(
    scenario => flagEnabledFamilyCashflowModelling || scenario.familyId == null,
  );
  const ownModels =
    scope.kind === 'family' ? visible : visible.filter(scenario => scenario.familyId == null);
  const familyModels =
    scope.kind === 'client' ? visible.filter(scenario => scenario.familyId != null) : [];
  const hasModels = visible.length > 0;

  const createModel = async (label: string, memberClientIds?: string[]) => {
    try {
      const body = {
        ...(label ? { label } : {}),
        ...(scope.kind === 'family' ? { memberClientIds } : {}),
      };
      const result = await createScenario(body);
      closeCreateModal();
      navigate(modelDetailPath(scope, result.scenario.id));
    } catch (error: unknown) {
      const questions = blockedSeedQuestions(error);
      if (questions) {
        closeCreateModal();
        setMissingDataQuestions(questions);
      } else {
        showError('The model could not be created. Please try again shortly.');
      }
    }
  };

  const archivedSwitch = (
    <Switch
      size="xs"
      label="Show archived"
      checked={showArchived}
      onChange={event => setShowArchived(event.currentTarget.checked)}
    />
  );
  const newModelButton = (
    <Button
      size="xs"
      variant="light"
      leftSection={<IconSparkles size={14} />}
      onClick={openCreateModal}
    >
      {hasModels ? 'New model' : 'Build a model'}
    </Button>
  );

  const emptyState = (
    <Card withBorder radius="md" p="lg">
      <Flex direction="column" align="flex-start" gap="xs">
        <IconChartLine size={24} stroke={1.5} />
        <Text fw={600}>No cashflow models yet</Text>
        <Text size="sm" c="dimmed">
          {scope.kind === 'family'
            ? 'Build one from the incomes, expenditure and life events recorded for the ' +
              'household — then explore the projection, charts and history here.'
            : 'Build one from the incomes, expenditure and life events recorded for this ' +
              'client — then explore the projection, charts and history here.'}
        </Text>
        {readiness && (
          <Box w="100%" mt="xs">
            <ModelDataCallout readiness={readiness} scope={scope} />
          </Box>
        )}
      </Flex>
    </Card>
  );

  return (
    <Box
      px={flagEnabledFamilyCashflowsRevamp ? undefined : 'md'}
      data-testid="cashflow-models-section"
    >
      {flagEnabledFamilyCashflowsRevamp ? (
        // The tab's other sections use the same small Add button in their headers.
        <ModelViews
          scenarios={visible}
          scope={scope}
          emptyState={emptyState}
          controls={
            <DesignSystemButton variant="tertiary" size="xs" onClick={openCreateModal}>
              {hasModels ? 'New model' : 'Build a model'}
            </DesignSystemButton>
          }
        />
      ) : (
        <Flex justify="space-between" align="center" mb="sm">
          <Text size="lg" fw={700}>
            Cashflow models
          </Text>
          <Flex gap="md" align="center">
            {hasModels && archivedSwitch}
            {newModelButton}
          </Flex>
        </Flex>
      )}

      {hasModels && !flagEnabledFamilyCashflowsRevamp && (
        <Stack gap="md">
          {ownModels.length > 0 && (
            <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
              {ownModels.map(scenario => (
                <ScenarioCard key={scenario.id} scenario={scenario} scope={scope} />
              ))}
            </SimpleGrid>
          )}
          {familyModels.length > 0 && (
            <Box data-testid="family-models-group">
              <Text size="sm" fw={600} c="dimmed" mb="xs">
                Family models
              </Text>
              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
                {familyModels.map(scenario => (
                  <ScenarioCard key={scenario.id} scenario={scenario} scope={scope} />
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Stack>
      )}
      {!hasModels && !flagEnabledFamilyCashflowsRevamp && emptyState}

      {memberModels.isError && (
        <Text size="sm" c="dimmed" mt="sm">
          Some family members&apos; own models could not be loaded, so they are missing from this
          list. Please try again shortly.
        </Text>
      )}

      <CreateModelModal
        // Remount on each open so the name and member selection start fresh.
        key={String(createModalOpen)}
        opened={createModalOpen}
        onClose={closeCreateModal}
        isFamily={scope.kind === 'family'}
        members={scope.kind === 'family' ? members : undefined}
        isCreating={isCreating}
        onConfirm={(label, memberClientIds) => void createModel(label, memberClientIds)}
        readiness={readiness}
        scope={scope}
      />

      <MissingDataModal
        questions={missingDataQuestions}
        isFamily={scope.kind === 'family'}
        onClose={() => setMissingDataQuestions(null)}
      />
    </Box>
  );
}

function MissingDataModal({
  questions,
  isFamily,
  onClose,
}: {
  questions: string[] | null;
  isFamily: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      opened={questions != null}
      onClose={onClose}
      title="More information needed"
      centered
      data-testid="missing-data-modal"
    >
      <Stack gap="sm">
        <Text size="sm">
          {isFamily
            ? "The model can't be built yet because some details are missing from the " +
              "household's profile:"
            : "The model can't be built yet because some details are missing from the " +
              "client's profile:"}
        </Text>
        <List size="sm" spacing="xs" icon={<IconInfoCircle size={16} />}>
          {(questions ?? []).map(question => (
            <List.Item key={question}>{question}</List.Item>
          ))}
        </List>
        <Text size="sm" c="dimmed">
          Record the missing details on the profile tab, then create the model again.
        </Text>
        <Flex justify="flex-end">
          <Button onClick={onClose}>Got it</Button>
        </Flex>
      </Stack>
    </Modal>
  );
}

function CreateModelModal({
  opened,
  onClose,
  isFamily,
  members,
  isCreating,
  onConfirm,
  readiness,
  scope,
}: {
  opened: boolean;
  onClose: () => void;
  isFamily: boolean;
  members?: SelectableMember[];
  isCreating: boolean;
  onConfirm: (label: string, memberClientIds?: string[]) => void;
  readiness: CashflowReadiness | null;
  scope: ScenarioScope;
}) {
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  // The engine's household covers at most two adults: bigger families choose
  // which members the model includes.
  const needsMemberSelection = isFamily && (members?.length ?? 0) > 2;
  const canConfirm = !needsMemberSelection || selected.length > 0;

  const toggle = (clientId: string, checked: boolean) =>
    setSelected(current =>
      checked ? [...current, clientId] : current.filter(id => id !== clientId),
    );

  const confirm = () => {
    const memberClientIds = needsMemberSelection
      ? selected
      : members?.map(member => member.clientId);
    onConfirm(label.trim(), memberClientIds);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create a cashflow model"
      centered
      data-testid="create-model-modal"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {isFamily
            ? "The model starts pre-filled with the household's recorded data. Its overview " +
              'lists anything missing, and Siesta can help you fill it in.'
            : "The model starts pre-filled with the client's recorded data. Its overview " +
              'lists anything missing, and Siesta can help you fill it in.'}
        </Text>
        {/* The modal is not part of the tab, so a tab that is showing its own empty state does not
            speak for it: this is where the warning matters most, on the thin profile it is for. */}
        {readiness && (
          <TabShowsItsOwnEmptyState value={false}>
            <ModelDataCallout readiness={readiness} scope={scope} compact />
          </TabShowsItsOwnEmptyState>
        )}
        <TextInput
          label="Model name"
          placeholder="e.g. Retirement base plan"
          value={label}
          onChange={event => setLabel(event.currentTarget.value)}
          data-autofocus
        />
        {needsMemberSelection && members && (
          <>
            <Text size="sm" c="dimmed">
              A household model covers up to two adults (plus their dependants). Choose which family
              members to include.
            </Text>
            {members.map(member => (
              <Checkbox
                key={member.clientId}
                label={member.name}
                checked={selected.includes(member.clientId)}
                disabled={selected.length >= 2 && !selected.includes(member.clientId)}
                onChange={event => toggle(member.clientId, event.currentTarget.checked)}
              />
            ))}
          </>
        )}
        <Flex justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button disabled={!canConfirm} loading={isCreating} onClick={confirm}>
            Create model
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}

function ScenarioCard({ scenario, scope }: { scenario: ScenarioSummary; scope: ScenarioScope }) {
  const currency = useCurrency();
  // A family model's canonical page lives on the family route, wherever the
  // card is rendered; individual models stay on the client route.
  const target =
    scenario.familyId != null
      ? modelDetailPath({ kind: 'family', id: scenario.familyId }, scenario.id)
      : modelDetailPath({ kind: 'client', id: scenario.clientId ?? scope.id }, scenario.id);

  // The card is the only way into the detail view, so it must be a real
  // link — keyboard focusable and announced, not a clickable div.
  return (
    <Card
      withBorder
      radius="md"
      p="md"
      component={Link}
      to={target}
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-testid="cashflow-model-card"
    >
      <Flex justify="space-between" align="flex-start" mb="xs">
        <Text fw={600} lineClamp={1}>
          {scenario.label}
        </Text>
        <Flex gap={4}>
          {scenario.familyId != null && (
            <Badge size="xs" variant="light" color="grape">
              Family
            </Badge>
          )}
          {scenario.isPrimary && (
            <Badge size="xs" variant="light">
              Primary
            </Badge>
          )}
          {scenario.archived && (
            <Badge size="xs" variant="light" color="gray">
              Archived
            </Badge>
          )}
          {scenario.syncMode === 'frozen' && (
            <Badge size="xs" variant="light" color="gray">
              Frozen
            </Badge>
          )}
          {scenario.hasPendingSync && (
            <Badge size="xs" variant="light" color="yellow">
              New profile data
            </Badge>
          )}
        </Flex>
      </Flex>

      {scenario.kpis ? (
        <Flex gap="lg" mb="xs">
          <Box>
            <Text size="xs" c="dimmed">
              Legacy at {horizonAge(scenario.kpis.horizonAge)} (today&apos;s money)
            </Text>
            <Text size="sm" fw={600}>
              {scenario.kpis.legacyReal != null
                ? formatSignedMoney(scenario.kpis.legacyReal, currency)
                : '—'}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">
              Money runs out
            </Text>
            <Text size="sm" fw={600}>
              {scenario.kpis.runsOutAtAge != null
                ? atAgeStartingSentence(scenario.kpis.runsOutAtAge)
                : NEVER_RUNS_OUT_TEXT}
            </Text>
          </Box>
        </Flex>
      ) : (
        <Text size="sm" c="dimmed" mb="xs">
          Not projected yet
        </Text>
      )}

      {scenario.latestCommitIntent && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          Last change: {scenario.latestCommitIntent}
        </Text>
      )}
    </Card>
  );
}
